import React, { useState, useEffect, useMemo } from 'react';
import { login } from '../services/api';
import { clearAuth, setAuthSession, setLicencaBloqueio } from '../services/authStorage';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Avatar,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { ThemeModeToggle, THEME_MODE_KEY, THEME_PALETTE_KEY, useThemeMode } from '../theme/ThemeModeProvider';

export const LOGIN_DOMAIN_KEY = 'zen.login.dominio';

/** Normaliza o slug digitado / vindo da URL. */
export function normalizarEstabelecimento(valor) {
  return (valor || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9_-]/g, '');
}

export function rotaLogin(estabelecimento) {
  const slug = normalizarEstabelecimento(estabelecimento);
  return slug ? `/${slug}/login` : '/';
}

// A API responde ora com string pura, ora com objeto de erro.
const textoErro = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  return data.mensagem || data.message || data.error || '';
};

function LoginShell({ children }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          right: -120,
          bottom: -120,
          width: 520,
          height: 520,
          backgroundImage: `url(${theme.zen.watermark})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          opacity: theme.zen.watermarkOpacity,
          pointerEvents: 'none',
        },
      }}
    >
      <ThemeModeToggle
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary' }}
      />

      <Card
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 400,
          padding: 3,
          boxShadow: 3,
          borderRadius: 5,
        }}
      >
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Avatar
              src={theme.zen.logo}
              alt="ZenGestão"
              sx={{ width: 224, height: 70, mb: 4 }}
              variant="rounded"
            />
          </Box>
          {children}
        </CardContent>
      </Card>

      <Typography
        variant="body2"
        sx={{
          mt: 4,
          color: 'text.secondary',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        Copyright © ZenSoftware 2025. Todos os direitos reservados.
        <br />
        <span style={{ fontSize: '0.8rem' }}>ZenSoftware</span>
      </Typography>
    </Box>
  );
}

function clearBrowserCache() {
  const themeMode = localStorage.getItem(THEME_MODE_KEY);
  const themePalette = localStorage.getItem(THEME_PALETTE_KEY);
  const dominioSalvo = localStorage.getItem(LOGIN_DOMAIN_KEY);
  const sidebarCollapsed = localStorage.getItem('zen.sidebar.collapsed');
  localStorage.clear();
  sessionStorage.clear();
  if (themeMode) localStorage.setItem(THEME_MODE_KEY, themeMode);
  if (themePalette) localStorage.setItem(THEME_PALETTE_KEY, themePalette);
  if (dominioSalvo) localStorage.setItem(LOGIN_DOMAIN_KEY, dominioSalvo);
  if (sidebarCollapsed != null) localStorage.setItem('zen.sidebar.collapsed', sidebarCollapsed);
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName);
      });
    });
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }
}

/** Passo 1: só o estabelecimento → avança para /{slug}/login */
function EstabelecimentoStep() {
  const navigate = useNavigate();
  const [estabelecimento, setEstabelecimento] = useState(
    () => localStorage.getItem(LOGIN_DOMAIN_KEY) || ''
  );
  const [error, setError] = useState('');

  useEffect(() => {
    clearBrowserCache();
  }, []);

  const handleAvancar = (e) => {
    e.preventDefault();
    setError('');
    const slug = normalizarEstabelecimento(estabelecimento);
    if (!slug) {
      setError('Informe o estabelecimento.');
      return;
    }
    localStorage.setItem(LOGIN_DOMAIN_KEY, slug);
    navigate(rotaLogin(slug));
  };

  return (
    <LoginShell>
      <form onSubmit={handleAvancar}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Informe o estabelecimento para continuar
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Estabelecimento"
            type="text"
            name="estabelecimento"
            value={estabelecimento}
            onChange={(e) => setEstabelecimento(e.target.value)}
            fullWidth
            required
            autoFocus
            autoComplete="organization"
            inputProps={{ autoCapitalize: 'none', spellCheck: false }}
            helperText="Ex.: minhaempresa"
          />
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Avançar
          </Button>
        </Stack>
      </form>
    </LoginShell>
  );
}

/** Passo 2: login + senha em /{estabelecimento}/login */
function CredenciaisStep() {
  const navigate = useNavigate();
  const { applyPreferences } = useThemeMode();
  const { estabelecimento: slugParam } = useParams();
  const dominio = useMemo(
    () => normalizarEstabelecimento(slugParam),
    [slugParam]
  );

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    clearBrowserCache();
    if (!dominio) {
      navigate('/', { replace: true });
      return;
    }
    localStorage.setItem(LOGIN_DOMAIN_KEY, dominio);
  }, [dominio, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!dominio) {
      setError('Estabelecimento inválido.');
      return;
    }
    setEnviando(true);
    try {
      const response = await login(dominio, email, senha);
      if (response.status === 200 && response.data.token) {
        const dominioSalvo = normalizarEstabelecimento(
          response.data.slug || dominio
        );
        if (dominioSalvo) localStorage.setItem(LOGIN_DOMAIN_KEY, dominioSalvo);

        setAuthSession({
          token: response.data.token,
          refreshToken: response.data.refreshToken || '',
          userName: response.data.userName,
          userId: response.data.userId,
          slug: response.data.slug,
          perfil: response.data.perfil || '',
          perfilId: response.data.perfilId,
          permissoes: response.data.permissoes || {},
          modulos: response.data.modulos || [],
          modulosAtivos: response.data.modulosAtivos || [],
          empresasPermitidas: response.data.empresasPermitidas || 1,
          estabelecimentosPermitidos: response.data.estabelecimentosPermitidos || [],
          acessoTodasFiliais: Boolean(response.data.acessoTodasFiliais),
        });
        setLicencaBloqueio('');
        applyPreferences({
          tema: response.data.tema,
          temaEscuro: response.data.temaEscuro,
        });
        navigate('/dashboard');
      } else {
        throw new Error('Credenciais inválidas');
      }
    } catch (err) {
      console.error('Erro de login:', err);
      if (err.response) {
        if (err.response.status === 401) {
          setError(
            textoErro(err.response.data) || 'Usuário ou senha inválida!'
          );
        } else if (err.response.status === 404) {
          setError('Usuário não encontrado!');
        } else {
          setError('Erro ao tentar logar. Tente novamente.');
        }
      } else {
        setError('Erro de conexão com o servidor.');
      }
      clearAuth();
    } finally {
      setEnviando(false);
    }
  };

  if (!dominio) return null;

  return (
    <LoginShell>
      <form onSubmit={handleLogin}>
        <Stack spacing={2}>
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Estabelecimento
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {dominio}
            </Typography>
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              variant="body2"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <ArrowBack fontSize="inherit" />
              Trocar estabelecimento
            </Link>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Login"
            type="text"
            name="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            autoFocus
            autoComplete="username"
          />
          <TextField
            label="Senha"
            type={mostrarSenha ? 'text' : 'password'}
            name="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            fullWidth
            required
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    edge="end"
                  >
                    {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={enviando}
          >
            {enviando ? 'Entrando…' : 'Entrar'}
          </Button>
        </Stack>
      </form>
    </LoginShell>
  );
}

/** `/` → estabelecimento; `/:estabelecimento/login` → credenciais. */
function Login() {
  const { estabelecimento } = useParams();
  if (estabelecimento) return <CredenciaisStep />;
  return <EstabelecimentoStep />;
}

export default Login;
